import { CircularProgress, Menu, MenuItem } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import { triggerExposureCalc } from '../../../hooks/useExposure';
import { useUser } from '../../../hooks/useUser';
import { ClearAccountWHSingleMaterial } from '../../../pages/AdminChanges/AdminUtils';
import { createFutureOrder } from '../../../sp-firebase/futureOrderFB';
import { updateAccountInventory } from '../../../sp-firebase/inventoryFB';
import { saveInventoryReport } from '../../../sp-firebase/inventoryReportFB';
import { updateUserAccount } from '../../../sp-firebase/userFB';
import { SpotLockUser } from '../../../sp-types/userType';
import {
  getColorsInventory,
  mockColorFullInventoryData,
  mockColorFullNewFutureOrderData,
} from '../../../utils/mockData';
import AccountSelector from '../../AccountSelector';
import { useAccountStore } from '../../Stores/accountStore';
import { useDemoModeStore } from '../../Stores/demoModeStore';
import { useFutureOrderStore } from '../../Stores/FutureOrdersStore';
import { useTopBarComponent } from '../../Stores/TopBarComponentStore';
import ButtonDesigned from '../../UI-Elements/Buttons/ButtonDesigned';
import { OptionSelect } from '../../UI-Elements/Select/OptionSelect';
import { AdminButtons } from './AdminButtons';
import { CaseSwitchers } from './CaseSwitchers';
import { OverlayDisplayDiv } from '../../UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import ChangePassword from '../../Auth/ChangePassword';

export const CURRENT_USE_CASES: Record<string, { account: string; allowReset?: boolean }> = {
  'Use case 1': { account: 'useCase1', allowReset: true },
  'Use case 2': { account: 'useCase2', allowReset: true },
  'Use case 3': { account: 'useCase3', allowReset: true },
};

interface Props {
  currentPage: string;
}

const TopBar: React.FC<Props> = ({ currentPage }) => {
  const { data: userData } = useUser();
  const { account } = useAccountStore();
  const { topBarActionComponent: component } = useTopBarComponent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentUseCase, setCurrentUseCase] = useState('');
  const [showAdminTools, setShowAdminTools] = useState(false); ///userData?.isAdmin);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const { isDemoMode, setIsDemoMode } = useDemoModeStore();
  const { resetStore } = useFutureOrderStore();

  React.useEffect(() => {
    if (account?.id) {
      const useCase = Object.entries(CURRENT_USE_CASES).find((item) => item[1].account === account.id)?.[0] || '';
      setCurrentUseCase(useCase);
    }
  }, [account]);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(false);

  const switchAccount = async (toAccount: string) => {
    if (account?.id !== toAccount) {
      setLoading(true);

      await updateUserAccount(userData?.id || '', toAccount);

      window.location.reload();
    }
  };

  const restart = async () => {
    setLoading(true);
    if (userData) {
      await ClearAccountWHSingleMaterial(account, userData);
      await triggerExposureCalc({
        accountId: account.id || '',
        materialIds: account.materialsToAccount.map((item) => item.materialId),
      });
    }
    // if (account.id === USE_CASE_COLORS) {
    //   await ClearAccountWHSingleMaterial(account, userData || ({} as SpotLockUser));
    //   await createFutureOrder(account.id, mockColorFullNewFutureOrderData('myIOrder'));
    //   await updateAccountInventory(account.id, mockColorFullInventoryData(account.id));
    //   await Promise.all(getColorsInventory(account.id).map((a) => saveInventoryReport(a, true)));
    //   await triggerExposureCalc({
    //     accountId: account.id || '',
    //     materialIds: account.materialsToAccount.map((item) => item.materialId),
    //   });
    // }
    setLoading(false);
    window.location.reload();
  };

  const logout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
    } catch (e) {
      console.error('Logout error:', e);
    }
    sessionStorage.removeItem('uid');
    navigate('/login');
  };

  const toggleDemo = () => {
    setShowAdminTools((show) => !show);
  };

  return (
    <div className="flex px-4 min-h-[3.75rem] items-center justify-between  shadow-md bg-white  overflow-x-hidden ">
      {userData?.isDemo ? (
        <h4 className={`flex font-normal my-auto truncate ${isDemoMode ? 'text-blue-600' : ''} `}>
          {currentPage} {isDemoMode && '- Demo Mode'}
        </h4>
      ) : (
        <h4 className="flex">{currentPage}</h4>
      )}
      <div className="flex flex-row space-x-2  ">
        {showChangePassword && (
          <OverlayDisplayDiv setDisplayOverlay={() => setShowChangePassword(false)} hideClose>
            <ChangePassword setActiveComponent={(_key: string) => setShowChangePassword(false)} />
          </OverlayDisplayDiv>
        )}

        {showAdminTools && userData && (
          <>
            <div className="hidden">
              <CaseSwitchers
                selectedState={currentUseCase}
                setState={setCurrentUseCase}
                states={Object.keys(CURRENT_USE_CASES)}
                userData={userData}
              />
            </div>
            <AdminButtons userId={userData?.id || ''} accountId={account?.id || ''} />
            <div title={`account: ${account?.id} isProd: ${(window as any).isProd}`} className="self-center hidden">
              <ButtonDesigned
                roundedValue="full"
                style="outline"
                onButtonClick={restart}
                className="self-center h-9  relative"
                disableButton={loading}
              >
                {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
                Restart <b>{account?.id}</b> Account!
              </ButtonDesigned>
            </div>
            <div className="hidden">
              <OptionSelect
                currentOption={isDemoMode ? 'Demo Mode' : 'Regular mode'}
                setCurrentOption={(value: string) => setIsDemoMode(value === 'Demo mode' ? true : false)}
                options={['Demo mode', 'Regular mode']}
              />
            </div>
          </>
        )}
      </div>
      <AccountSelector />

      <div className="flex flex-row space-x-8  ">
        {component}
        <div className="flex items-center space-x-4">
          <div className="text-gray-700 text-sm">{account?.id}</div>
          <button
            className="h-9 w-9 flex items-center justify-center"
            title="Chat"
            onClick={() => {
              toast.info('Chat is currently unavailable');
            }}
          >
            <img className="h-7 w-7" src={`/assets/icons/colored/chat-blue.svg`} alt="" />
          </button>
          <button
            className="rounded-full border-2 border-blue-300 h-9 w-9 flex items-center justify-center"
            title="Help"
            onClick={() => {}}
          >
            <img className="h-5 w-5" src={`/assets/icons/colored/help.svg`} alt="" />
          </button>

          {/* User info section */}
          <div
            ref={anchorRef}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(true)}
          >
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium text-gray-800">
                {userData?.name || userData?.email?.split('@')[0] || 'User'}
              </span>
              <span
                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full mt-1"
                style={{
                  backgroundColor:
                    userData?.role === 'superadmin'
                      ? '#f3e8ff'
                      : userData?.role === 'admin' || userData?.isAdmin
                      ? '#fef3c7'
                      : userData?.role === 'manager'
                      ? '#dcfce7'
                      : '#f3f4f6',
                  color:
                    userData?.role === 'superadmin'
                      ? '#7c3aed'
                      : userData?.role === 'admin' || userData?.isAdmin
                      ? '#b45309'
                      : userData?.role === 'manager'
                      ? '#15803d'
                      : '#4b5563',
                }}
              >
                {userData?.role === 'superadmin'
                  ? 'Super Admin'
                  : userData?.role === 'admin' || userData?.isAdmin
                  ? 'Admin'
                  : userData?.role === 'manager'
                  ? 'Manager'
                  : userData?.role === 'editor'
                  ? 'Editor'
                  : 'User'}
              </span>
            </div>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <Menu open={isMenuOpen} onBlur={() => setIsMenuOpen(false)} anchorEl={anchorRef.current}>
            {userData?.isAdmin && (
              <MenuItem onClick={toggleDemo}>{showAdminTools ? 'Hide' : 'Show'} admin tools</MenuItem>
            )}
            {/* Change password hidden for now
            <MenuItem title={`account: ${account?.id}`} onClick={() => setShowChangePassword(true)}>
              Change password
            </MenuItem>
            */}
            <MenuItem onClick={logout}>Logout</MenuItem>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
